// QR Table Pay › QR Codes (SCR-106; legacy QRTablePayList.vue, QRCodeItem.vue, `qr_codes`; field note
// backoffice.qr-table-pay): a code on one of the location's tables, a floor-plan table or a free table, that opens the
// online store at that table. The code's text is its `url`: the server writes it from the store's subdomain and the
// table, in legacy's shape `https://{subdomain}.parsepronto.com?tableId=<table>` (Direct plan, "Contracts Direct must
// honour" 2). A code made here names the Radish Next table id; an imported code keeps legacy's stored URL word for word,
// whose `tableId` is the legacy table's hashid, resolved through `sync.legacy_refs` (coordinator 2026-10-21, option a).
// The PNG is drawn in the browser from the url, colours and margin; legacy's stored `data_url` is not kept.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { FreeTableId, LocationId, QrCodeId, TableId } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten, ProposalRequired } from '../config/index.ts';

/** Legacy StoreQrCodeRequest's colour rule: `#rgb` or `#rrggbb`. */
const HexColor = z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/);

/** The table a code opens: a table on a floor plan (legacy "Table mapping integration" on) or a free table (off). */
export const QrCodeTable = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('floor'), tableId: TableId }),
    z.object({ kind: z.literal('free'), freeTableId: FreeTableId }),
  ])
  .meta({ id: 'QrCodeTable' });

/** What a save may change. */
export const QrCodeChanges = z
  .object({
    name: z.string().trim().min(1).max(255),
    table: QrCodeTable,
    /** Legacy default white. */
    backgroundColor: HexColor,
    /** Legacy default black: the code's modules. */
    foregroundColor: HexColor,
    /** The quiet zone in modules; the editor's slider runs 0 to 20, legacy's API took up to 100. */
    margin: z.int().min(0).max(100),
  })
  .meta({ id: 'QrCodeChanges' });

export const QrCodeFields = QrCodeChanges.extend({
  /** The text the code encodes (legacy `url_attribute`). */
  url: z.string().min(1).max(255),
  /** The imported code's own legacy hashid; null for a code made in Radish Next. */
  legacyHashid: z.string().max(64).nullable(),
}).meta({ id: 'QrCodeFields' });

/** The table as the card shows it: its number and its zone's prefix (B791: a free table's too); null once the table is gone. */
export const QrCodeTableLabel = z
  .object({ tableNumber: z.int().nullable(), zonePrefix: z.string().nullable() })
  .meta({ id: 'QrCodeTableLabel' });

export const QrCodeView = QrCodeFields.extend({
  qrCodeId: QrCodeId,
  locationId: LocationId,
  version: z.int().nonnegative(),
  tableLabel: QrCodeTableLabel.nullable(),
}).meta({ id: 'QrCodeView' });

/** A table a code can be put on, as the editor's Table select lists it. */
export const QrTableOption = z
  .object({ table: QrCodeTable, tableNumber: z.int().nullable(), zonePrefix: z.string().nullable() })
  .meta({ id: 'QrTableOption' });

export const QrCodeNotFound = defineError({
  code: 'online.qr_code_not_found', httpStatus: 404, message: 'errors.online.qr_code_not_found', retryable: false,
  details: z.object({ qrCodeId: QrCodeId }),
});

/**
 * A save the server refuses: `table_not_found` (not a live table of the location), `no_online_store` (no subdomain to
 * point at), `subdomain_invalid` (the store's subdomain is no single DNS label), `legacy_import_only`, and
 * `legacy_text_unusable` (the import's text is no QR table code's: core isQrCodeText).
 */
export const QrCodeInvalid = defineError({
  code: 'online.qr_code_invalid', httpStatus: 422, message: 'errors.online.qr_code_invalid', retryable: false,
  details: z.object({ rule: z.string(), field: z.string().optional() }),
});

// Cloud-only (review of #1861): a code's text decides where a printed sticker opens, so the cloud alone writes codes and a
// node's fact on one is refused (NODE_WRITES). The Local Backoffice shows "Needs internet" rather than saving a code the
// cloud would refuse.
const common = {
  availability: 'cloud', origin: 'cloud',
  cloudReason: "A QR code's text decides where every printed copy opens, so only the cloud writes codes; a location node's write is refused.",
  emits: [ConfigFieldsWritten], errors: [QrCodeInvalid, QrCodeNotFound, CloudAuthoritative, ProposalRequired], dryRun: true,
} as const;

export const UpsertQrCode = defineCommand({
  ...common,
  name: 'online.upsertQrCode',
  summary: "Create a QR Table Pay code on one of the location's tables, or change its name, table, colours or margin.",
  input: z.object({
    qrCodeId: QrCodeId,
    locationId: LocationId,
    changes: QrCodeChanges.partial(),
    /** The migration import only (any other session is refused): legacy's stored URL and the code's hashid, kept as they are. */
    legacy: z.object({ url: z.string().min(1).max(255), hashid: z.string().max(64).nullable() }).optional(),
  }),
  output: QrCodeView,
  permission: 'qr.manage',
  risk: 'low',
  agent: { expose: false },
  legacy: ['POST /v1/online-store/{id}/qr-codes', 'PUT /v1/online-store/{id}/qr-codes/{qr}'],
});

export const DeleteQrCode = defineCommand({
  ...common,
  name: 'online.deleteQrCode',
  summary: 'Delete a QR Table Pay code. Printed copies stop opening the table only if the table goes too.',
  input: z.object({ qrCodeId: QrCodeId, locationId: LocationId }),
  output: z.object({ id: QrCodeId, deleted: z.literal(true) }),
  permission: 'qr.manage',
  risk: 'low',
  agent: { expose: false },
  legacy: ['DELETE /v1/online-store/{id}/qr-codes/{qr}'],
});

export const ListQrCodes = defineQuery({
  name: 'online.qrCodes.list',
  summary: "The location's QR Table Pay codes, oldest first, each with its table's number and zone, and the tables a code can go on.",
  input: z.object({ locationId: LocationId }),
  output: z.object({
    qrCodes: z.array(QrCodeView),
    /** The online store's subdomain a new code points under; null when the location has no store (no code can be made). */
    subdomain: z.string().nullable(),
    /** The location's live tables: floor-plan tables (legacy's "Table mapping integration" on) and free tables, by number. */
    tables: z.array(QrTableOption),
  }),
  permission: 'qr.manage',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/online-store/{id}/qr-codes', 'GET /v1/online-store/{id}/qr-codes/{qr}'],
});

export const qrCodeCommands = [UpsertQrCode, DeleteQrCode] as const;
export const qrCodeQueries = [ListQrCodes] as const;
